import React, { useState } from "react";
import { useFormik } from "formik";
import { useDispatch } from "react-redux";
import { showToast } from "Redux/General/Actions";
import { Api } from "Helpers";

import Header from "Components/Header/Header";
import MobileHeader from "Components/Header/MobileHeader";
import Footer from "Components/Footer/Footer";
import PageHeading from "Components/PageHeading/PageHeading";
import { ContactUsPageContainer } from "./ContactUs.style";
import CustomButton from "Components/Commons/CustomButton/CustomButton";

import { ReactComponent as PhoneIcon } from "../../../Assets/Images/Icons/phone_icon.svg";
import { ReactComponent as MailIcon } from "../../../Assets/Images/Icons/at_icon.svg";
import { ReactComponent as FacebookIcon } from "../../../Assets/Images/Icons/fb_icon.svg";
import { ReactComponent as TickTockIcon } from "../../../Assets/Images/Icons/tiktok_icon.svg";
import { ReactComponent as InstaIcon } from "../../../Assets/Images/Icons/insta_icon.svg";
import { ReactComponent as ShieldIcon } from "../../../Assets/Images/Icons/lock_icon.svg";


import * as yup from "yup";

/* ---------------- Validation Helper ---------------- */
const generateValidationSchema = (inputs) => {
    const shape = {};

    inputs.forEach((input) => {
        const { name, type, validation = {} } = input; // ✅ safe default
        let schema = yup.string();

        if (validation.required) {
            schema = schema.required(`${input.label || name} is required`);
        }

        if (validation.email) {
            schema = schema.email("Invalid email format");
        }

        if (type === "number") {
            schema = yup
                .number()
                .typeError(`${input.label || name} must be a number`);
        }

        shape[name] = schema;
    });

    return yup.object().shape(shape);
};

/* ---------------- Input Fields ---------------- */
const inputs = [
    { label: "First Name", name: "firstName", validation: { required: true } },
    { label: "Last Name", name: "lastName", validation: { required: true } },
    { label: "School Name", name: "schoolName", validation: { required: false } },
    { label: "District Name", name: "districtName", validation: { required: false } },
    { label: "Email Address", name: "email", type: "email", validation: { required: true, email: true } },
    { label: "Phone Number", name: "phone", validation: { required: false } },
    { label: "Message", name: "message", validation: { required: true } },
];

const validationSchema = generateValidationSchema(inputs);

const ContactUsPage = () => {
    const dispatch = useDispatch();
    const [isLoading, setIsLoading] = useState(false);

    const formik = useFormik({
        initialValues: {
            firstName: "",
            lastName: "",
            schoolName: "",
            districtName: "",
            email: "",
            phone: "",
            message: "",
        },
        validationSchema,
        onSubmit: async (values, { resetForm }) => {
            try {
                setIsLoading(true);

                // Convert message to HTML like old page
                let htmlContent =
                    "<p>" + values?.message.trim().replace(/\n/g, "</p><p>") + "</p><br />";

                const payload = {
                    name: values?.firstName?.trim() || "",
                    lastName: values?.lastName?.trim() || "",
                    schoolName: values?.schoolName?.trim() || "",
                    districtName: values?.districtName?.trim() || "",
                    email: values?.email?.trim() || "",
                    phoneNumber: values?.phone?.trim() || "",
                    message: htmlContent,
                };

                const response = await new Api().post("contact-us", {
                    data: payload,
                    skipAuth: true,
                });

                if (response.status === 200) {
                    dispatch(showToast("Your message has been sent successfully!"));
                    resetForm();
                }
            } catch (error) {
                console.error("Contact form submission error:", error);
                dispatch(showToast("Something went wrong. Please try again later."));
            } finally {
                setIsLoading(false);
            }
        },
    });

    return (
        <ContactUsPageContainer>
            <Header />
            <MobileHeader />

            <PageHeading
                title={
                    <>
                        Contact <span className="redfont">Us</span>
                    </>
                }
                breadcrumbs={[
                    { label: "Home", path: "/" },
                    { label: "Contact Us" },
                ]}
            />

            <div className="contact_us_form">
                <div className="mygrid-container">
                    <div className="mygrid-row">
                        {/* Left Section */}
                        <div className="mygrid-col-sm-12 mygrid-col-md-12 mygrid-col-xl-5">
                            <div className="contact_us-content">
                                <h2>Quick Contact</h2>
                                <h4>We’d Love to Hear From You</h4>
                                <p>
                                    Have questions, ideas or need support? We’re just a message
                                    away.
                                </p>

                                <ul className="contact_list">
                                    <li>
                                        <PhoneIcon className="icon" />
                                        <span><a href="tel:9094092913" title="Call Us">909-409-2913</a></span>
                                    </li>
                                    <li>
                                        <MailIcon className="icon" />
                                        <span><a href="mailto:tferguson@subjectsmaster.com" title="Mail Us">tferguson@subjectsmaster.com</a></span>
                                    </li>
                                </ul>
                                <div className="social_icons_contact">
                                    <h4>Social Media:</h4>
                                    <ul className="social_links">
                                        <li>
                                            <a
                                                href="https://www.facebook.com/subjectsmaster?mibextid=LQQJ4d"
                                                target="_blank"
                                                rel="noopener noreferrer" title="Facebook"
                                            >
                                                <FacebookIcon className="icon" />
                                            </a>
                                        </li>
                                        <li>
                                            <a
                                                href="https://www.instagram.com/subjectsmaster?igsh=OGQ5ZDc2ODk2ZA=="
                                                target="_blank"
                                                rel="noopener noreferrer" title="Instagram"
                                            >
                                                <InstaIcon className="icon" />
                                            </a>
                                        </li>
                                        <li>
                                            <a
                                                href="https://www.tiktok.com/@subjectsmaster?_t=8iVpTGlOxLy&_r=1"
                                                target="_blank"
                                                rel="noopener noreferrer" title="TikTok"
                                            >
                                                <TickTockIcon className="icon" />
                                            </a>
                                        </li>
                                    </ul>
                                </div>

                            </div>
                        </div>

                        {/* Right Section Form */}
                        <div className="mygrid-col-sm-12 mygrid-col-md-12 mygrid-col-xl-7">
                            <div className="contact_us-form-data">
                                <form onSubmit={formik.handleSubmit}>
                                    <div className="mygrid-row">
                                        <div className="mygrid-col-md-6 mygrid-col-xs-6">
                                            <label>First Name</label>
                                            <input
                                                type="text"
                                                name="firstName"
                                                placeholder="Enter first name"
                                                onChange={formik.handleChange}
                                                value={formik.values.firstName}
                                            />
                                            <span className="error">
                                                {formik.touched.firstName && formik.errors.firstName}
                                            </span>
                                        </div>
                                        <div className="mygrid-col-md-6 mygrid-col-xs-6">
                                            <label>Last Name</label>
                                            <input
                                                type="text"
                                                name="lastName"
                                                placeholder="Enter last name"
                                                onChange={formik.handleChange}
                                                value={formik.values.lastName}
                                            />
                                            <span className="error">
                                                {formik.touched.lastName && formik.errors.lastName}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="mygrid-row">
                                        <div className="mygrid-col-md-6 mygrid-col-xs-6">
                                            <label>School Name</label>
                                            <input
                                                type="text"
                                                name="schoolName"
                                                placeholder="Enter school name"
                                                onChange={formik.handleChange}
                                                value={formik.values.schoolName}
                                            />
                                            <span className="error">
                                                {formik.touched.schoolName && formik.errors.schoolName}
                                            </span>
                                        </div>
                                        <div className="mygrid-col-md-6 mygrid-col-xs-6">
                                            <label>District Name</label>
                                            <input
                                                type="text"
                                                name="districtName"
                                                placeholder="Enter district name"
                                                onChange={formik.handleChange}
                                                value={formik.values.districtName}
                                            />
                                            <span className="error">
                                                {formik.touched.districtName && formik.errors.districtName}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="mygrid-row">
                                        <div className="mygrid-col-md-6 mygrid-col-xs-6">
                                            <label>Email Address</label>
                                            <input
                                                type="email"
                                                name="email"
                                                placeholder="Enter email address"
                                                onChange={formik.handleChange}
                                                value={formik.values.email}
                                            />
                                            <span className="error">
                                                {formik.touched.email && formik.errors.email}
                                            </span>
                                        </div>
                                        <div className="mygrid-col-md-6 mygrid-col-xs-6">
                                            <label>Phone Number</label>
                                            <input
                                                type="tel"
                                                name="phone"
                                                placeholder="Enter phone number"
                                                onChange={formik.handleChange}
                                                value={formik.values.phone}
                                            />
                                            <span className="error">
                                                {formik.touched.phone && formik.errors.phone}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="mygrid-row">
                                        <div className="mygrid-col-md-12 mygrid-col-xs-12">
                                            <label>Message</label>
                                            <textarea
                                                name="message"
                                                placeholder="Enter message"
                                                onChange={formik.handleChange}
                                                value={formik.values.message}
                                            />
                                            <span className="error">
                                                {formik.touched.message && formik.errors.message}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="form_submit_section mygrid-row">
                                        <div className="mygrid-col-md-12 flex-between">
                                            <CustomButton
                                                text={isLoading ? "Sending..." : "Submit"}
                                                className="submit_btn"
                                                type="submit"
                                                isLoading={isLoading}
                                            />
                                            <div className="anti_spam_note">
                                                <ShieldIcon className="icon" />
                                                <span>
                                                    We hate spam and promise to keep your email safe.
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <Footer isCTAHidden={true} />
        </ContactUsPageContainer>
    );
};

export default ContactUsPage;
